import * as React from "react";
import { useState } from "react";

import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles, useTheme, fade } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import SendIcon from "@material-ui/icons/Send";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import InfoIcon from "@material-ui/icons/Info";
import PersonIcon from "@material-ui/icons/PersonOutline";
import ReceiptIcon from "@material-ui/icons/ReceiptOutlined";
import LockIcon from "@material-ui/icons/LockOutlined";
import CloseIcon from "@material-ui/icons/Close";

import { Notification, useNotify } from "react-admin";

import { ENDPOINTS } from "../apiConstants";
import { getConfigPost, getConfigGet } from "../getConfig";

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const HISTORY_PAGE_SIZE = 10;

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accent = theme.palette.primary.main;
  const paper = theme.palette.background.paper;
  const inset = isDark ? "rgba(255,255,255,0.04)" : fade(accent, 0.04);
  const line = theme.palette.divider;
  const ok = isDark ? "#4ade80" : "#2e7d32";
  return {
    wrap: { width: "100%", margin: "0 auto", padding: theme.spacing(1, 2, 6) },
    band: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 16,
      background: `linear-gradient(135deg, ${fade(accent, 0.14)}, ${fade(theme.palette.secondary.main, 0.1)})`,
      border: `1px solid ${line}`,
      borderRadius: 18,
      padding: theme.spacing(2.75, 3.25),
      position: "relative",
      overflow: "hidden",
    },
    bandBadge: {
      flex: "none", width: 52, height: 52, borderRadius: 15, display: "grid", placeItems: "center",
      background: paper, border: `1px solid ${line}`, color: accent, boxShadow: theme.shadows[1],
      [theme.breakpoints.down("xs")]: { display: "none" },
    },
    eyebrow: { color: accent, fontWeight: 700, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" },
    bandTitle: { margin: "8px 0 3px", fontSize: 24, fontWeight: 700, letterSpacing: "-0.01em", color: theme.palette.text.primary },
    bandSub: { margin: 0, color: theme.palette.text.secondary, fontSize: 13, maxWidth: "62ch" },

    rail: { display: "flex", alignItems: "center", margin: theme.spacing(2.5, 0.25, 3), flexWrap: "wrap", rowGap: 12 },
    step: { display: "flex", alignItems: "center", gap: 11 },
    stepNum: {
      flex: "none", width: 26, height: 26, borderRadius: "50%", display: "grid", placeItems: "center",
      fontFamily: MONO, fontSize: 12.5, fontWeight: 700, border: `1.5px solid ${line}`,
      color: theme.palette.text.secondary, background: paper,
    },
    stepNumDone: { background: ok, borderColor: ok, color: "#fff" },
    stepNumActive: { background: accent, borderColor: accent, color: "#fff" },
    stepLbl: { fontSize: 13, fontWeight: 600, color: theme.palette.text.secondary, whiteSpace: "nowrap" },
    stepLblOn: { color: theme.palette.text.primary },
    railBar: { flex: "0 0 72px", height: 2, margin: "0 18px", background: line, borderRadius: 2 },

    card: { background: paper, border: `1px solid ${line}`, borderRadius: 18, boxShadow: theme.shadows[1], marginBottom: 18, overflow: "hidden" },
    cardLocked: { opacity: 0.72 },
    chead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: theme.spacing(1.75, 2.5), borderBottom: `1px solid ${line}` },
    cheadTitle: { margin: 0, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: theme.palette.text.secondary },
    cbody: { padding: theme.spacing(2.5) },

    tiles: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, margin: theme.spacing(2.25, 0, 0.5), [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr 1fr" } },
    tile: { border: `1px solid ${line}`, background: inset, borderRadius: 12, padding: theme.spacing(1.5, 1.75) },
    tileK: { fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: theme.palette.text.secondary, marginBottom: 7, display: "flex", alignItems: "center", gap: 5 },
    tileV: { fontFamily: MONO, fontSize: 15, fontWeight: 600, color: theme.palette.text.primary },

    pill: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, padding: "3px 9px", borderRadius: 999 },
    pillDot: { width: 6, height: 6, borderRadius: "50%", background: "currentColor" },

    verdict: { display: "flex", alignItems: "center", gap: 13, padding: theme.spacing(1.5, 2), borderRadius: 12, marginTop: 14, border: "1px solid transparent" },
    verdictT: { fontWeight: 700, fontFamily: MONO, fontSize: 13 },
    verdictS: { color: theme.palette.text.primary, fontSize: 12.5, opacity: 0.85 },

    drop: { display: "block", width: "100%", border: `1.5px dashed ${line}`, borderRadius: 12, background: inset, padding: theme.spacing(3, 2.5), textAlign: "center", color: theme.palette.text.secondary, cursor: "pointer", transition: "border-color .15s, background .15s", "&:hover": { borderColor: accent, background: fade(accent, 0.06) } },
    dropIcon: { color: accent, fontSize: 28 },
    files: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 },
    fchip: { display: "inline-flex", alignItems: "center", gap: 8, fontFamily: MONO, fontSize: 12, color: theme.palette.text.primary, background: inset, border: `1px solid ${line}`, borderRadius: 999, padding: "5px 8px 5px 11px" },
    fchipName: { cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted", "&:hover": { color: accent } },
    fchipX: { width: 16, height: 16, borderRadius: "50%", background: line, color: theme.palette.text.secondary, display: "grid", placeItems: "center", cursor: "pointer" },

    flabel: { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: theme.palette.text.secondary, marginBottom: 6, display: "block" },
    lockedHint: { display: "flex", alignItems: "center", gap: 9, marginBottom: 16, padding: theme.spacing(1.25, 1.75), borderRadius: 12, background: inset, border: `1px solid ${line}`, color: theme.palette.text.secondary, fontSize: 12.75 },
    cfoot: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: theme.spacing(1.875, 2.5), borderTop: `1px solid ${line}`, flexWrap: "wrap" },
    hint: { color: theme.palette.text.secondary, fontSize: 12.5 },

    comboWrap: { position: "relative" },
    menu: { position: "absolute", zIndex: 30, top: 42, left: 0, right: 0, maxHeight: 268, overflowY: "auto", borderRadius: 12, border: `1px solid ${line}` },
    option: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "9px 13px", cursor: "pointer", fontSize: 13.5, borderBottom: `1px solid ${line}`, "&:hover": { background: inset } },
    optName: { color: theme.palette.text.primary, fontWeight: 500 },
    optId: { fontFamily: MONO, fontSize: 12, color: theme.palette.text.secondary, flex: "none" },
    menuEmpty: { padding: "12px 13px", color: theme.palette.text.secondary, fontSize: 13 },

    result: { display: "flex", alignItems: "center", gap: 13, borderRadius: 18, padding: theme.spacing(2, 2.5), boxShadow: theme.shadows[1] },
    resultT: { fontWeight: 700, fontSize: 16 },
    resultS: { color: theme.palette.text.primary, fontSize: 12.5, opacity: 0.85 },

    tabs: { display: "inline-flex", background: inset, border: `1px solid ${line}`, borderRadius: 12, padding: 3, gap: 3, margin: theme.spacing(0, 0, 2) },
    tab: { border: 0, background: "transparent", color: theme.palette.text.secondary, font: "inherit", fontSize: 13, fontWeight: 600, padding: "7px 18px", borderRadius: 9, cursor: "pointer" },
    tabOn: { background: paper, color: theme.palette.text.primary, boxShadow: theme.shadows[1] },
    tblWrap: { overflowX: "auto", border: `1px solid ${line}`, borderRadius: 12 },
    tbl: { width: "100%", borderCollapse: "collapse", fontSize: 13, color: theme.palette.text.primary },
    th: { textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: accent, padding: "9px 12px", borderBottom: `1px solid ${line}`, background: fade(accent, isDark ? 0.14 : 0.06) },
    td: { padding: "9px 12px", borderBottom: `1px solid ${line}`, fontFamily: MONO, fontSize: 12.5, whiteSpace: "nowrap", color: theme.palette.text.primary },
    summaryRow: { display: "flex", gap: 16, alignItems: "center", fontSize: 13, flexWrap: "wrap", color: theme.palette.text.primary },
    detailCell: { padding: theme.spacing(1.75, 2), borderBottom: `1px solid ${line}`, background: inset },
    detailGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 },
    detailItem: { display: "flex", flexDirection: "column", gap: 3 },
    detailK: { fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: theme.palette.text.secondary },
    detailV: { fontSize: 13, color: theme.palette.text.primary, wordBreak: "break-word" },
    optionBox: { border: `1px solid ${line}`, borderRadius: 12, padding: theme.spacing(2), marginBottom: 14, background: inset },
    optionHead: { display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" },
    optionTag: { fontSize: 10, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#fff", background: accent, borderRadius: 999, padding: "3px 9px" },
    optionTitle: { fontSize: 14, fontWeight: 700, color: theme.palette.text.primary },
    optionSteps: { fontSize: 12.75, color: theme.palette.text.secondary, lineHeight: 1.7 },
    optionSummary: { cursor: "pointer", fontSize: 13, fontWeight: 700, color: accent, userSelect: "none" },
  };
});

// Minimal CSV parse — quoted fields with embedded commas are honoured.
function splitCsvLine(line: string): string[] {
  const out: string[] = []; let cur = ""; let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) { if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += c; }
    else { if (c === ",") { out.push(cur); cur = ""; } else if (c === '"') q = true; else cur += c; }
  }
  out.push(cur); return out;
}
function parseCsv(text: string): Array<Record<string, string>> {
  const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim().length);
  if (lines.length < 2) return [];
  const headers = splitCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((line) => {
    const cells = splitCsvLine(line);
    const row: Record<string, string> = {};
    const last = headers.length - 1;
    headers.forEach((h, i) => {
      // The last column (attachment) absorbs any trailing cells, so comma-separated
      // multiple proofs (a.pdf,b.pdf) survive instead of spilling into phantom columns.
      row[h] = i === last
        ? cells.slice(i).map((c) => c.trim()).filter(Boolean).join(";")
        : (cells[i] || "").trim();
    });
    return row;
  });
}

// --- Tiny store-only ZIP writer (avoids a jszip dependency for sample downloads) ---
const strToBytes = (s: string): Uint8Array => new TextEncoder().encode(s);
const b64ToBytes = (b64: string): Uint8Array => {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};
const crc32 = (bytes: Uint8Array): number => {
  let c = ~0;
  for (let i = 0; i < bytes.length; i++) {
    c ^= bytes[i];
    for (let k = 0; k < 8; k++) c = (c >>> 1) ^ (0xedb88320 & -(c & 1));
  }
  return ~c >>> 0;
};
const buildZip = (entries: Array<{ name: string; data: Uint8Array }>): Blob => {
  const chunks: Uint8Array[] = [];
  let offset = 0;
  const enc = (n: number, bytes: number): Uint8Array => {
    const a = new Uint8Array(bytes);
    for (let i = 0; i < bytes; i++) a[i] = (n >>> (i * 8)) & 0xff;
    return a;
  };
  const push = (a: Uint8Array) => { chunks.push(a); offset += a.length; };
  const records: Array<{ nameBytes: Uint8Array; crc: number; size: number; at: number }> = [];

  for (const e of entries) {
    const nameBytes = strToBytes(e.name);
    const crc = crc32(e.data);
    const size = e.data.length;
    const at = offset;
    push(enc(0x04034b50, 4)); push(enc(20, 2)); push(enc(0, 2)); push(enc(0, 2));
    push(enc(0, 2)); push(enc(0, 2)); push(enc(crc, 4)); push(enc(size, 4)); push(enc(size, 4));
    push(enc(nameBytes.length, 2)); push(enc(0, 2)); push(nameBytes); push(e.data);
    records.push({ nameBytes, crc, size, at });
  }

  const centralStart = offset;
  for (const r of records) {
    push(enc(0x02014b50, 4)); push(enc(20, 2)); push(enc(20, 2)); push(enc(0, 2)); push(enc(0, 2));
    push(enc(0, 2)); push(enc(0, 2)); push(enc(r.crc, 4)); push(enc(r.size, 4)); push(enc(r.size, 4));
    push(enc(r.nameBytes.length, 2)); push(enc(0, 2)); push(enc(0, 2)); push(enc(0, 2)); push(enc(0, 2));
    push(enc(0, 4)); push(enc(r.at, 4)); push(r.nameBytes);
  }
  const centralSize = offset - centralStart;
  push(enc(0x06054b50, 4)); push(enc(0, 2)); push(enc(0, 2));
  push(enc(records.length, 2)); push(enc(records.length, 2));
  push(enc(centralSize, 4)); push(enc(centralStart, 4)); push(enc(0, 2));

  return new Blob(chunks as BlobPart[], { type: "application/zip" });
};

// --- ZIP reader: handles stored + deflated entries via the native DecompressionStream ---
async function inflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  const DS = (window as any).DecompressionStream;
  if (!DS) throw new Error("This browser can't unzip compressed files — upload the files individually instead.");
  const stream = new Blob([bytes]).stream().pipeThrough(new DS("deflate-raw"));
  const ab = await new Response(stream as any).arrayBuffer();
  return new Uint8Array(ab);
}

async function readZip(buf: ArrayBuffer): Promise<Array<{ name: string; data: Uint8Array }>> {
  const dv = new DataView(buf);
  const u8 = new Uint8Array(buf);
  let eocd = -1;
  for (let i = u8.length - 22; i >= 0; i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error("That doesn't look like a valid .zip file.");
  const count = dv.getUint16(eocd + 10, true);
  let off = dv.getUint32(eocd + 16, true);
  const out: Array<{ name: string; data: Uint8Array }> = [];
  const dec = new TextDecoder();
  for (let n = 0; n < count && off + 46 <= u8.length; n++) {
    if (dv.getUint32(off, true) !== 0x02014b50) break;
    const method = dv.getUint16(off + 10, true);
    const compSize = dv.getUint32(off + 20, true);
    const nameLen = dv.getUint16(off + 28, true);
    const extraLen = dv.getUint16(off + 30, true);
    const commentLen = dv.getUint16(off + 32, true);
    const localOff = dv.getUint32(off + 42, true);
    const name = dec.decode(u8.subarray(off + 46, off + 46 + nameLen));
    const lNameLen = dv.getUint16(localOff + 26, true);
    const lExtraLen = dv.getUint16(localOff + 28, true);
    const dataStart = localOff + 30 + lNameLen + lExtraLen;
    const comp = u8.subarray(dataStart, dataStart + compSize);
    off += 46 + nameLen + extraLen + commentLen;
    if (name.endsWith("/")) continue;
    if (method === 0) out.push({ name, data: comp });
    else if (method === 8) out.push({ name, data: await inflateRaw(comp) });
  }
  return out;
}

// 1x1 placeholder proof files (agents replace them with the real receipt/screenshot).
const SAMPLE_PNG_B64 = "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==";
const SAMPLE_JPG_B64 = "/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAP//////////////////////////////////////////////////////////////////////////////////////wAALCAABAAEBAREA/8QAFAABAAAAAAAAAAAAAAAAAAAAAP/EABQQAQAAAAAAAAAAAAAAAAAAAAD/2gAIAQEAAD8AfwD/2Q==";
const SAMPLE_CSV = [
  "email,merchant,date,amount,orderid,description,attachment",
  "a.jones@gmail.com,Wowcher,24/04/2026,45.00,WOW-33845,Missing cashback,wow-33845.png",
  "m.patel@outlook.com,Boots,02/05/2026,12.99,BT-99012,Missing cashback,boots-99012.jpg",
].join("\n");
const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

interface IVdxCheckResponse {
  is_vdx_brand: boolean;
  brand: { canonical_name?: string; merchant_id?: string; match_confidence?: string } | null;
  user: { found: boolean; sprive_plus_active: boolean; vdx_member_id: string | null } | null;
  purchase: {
    already_tracked: boolean;
    business_days_since_purchase: number | null;
    within_tracking_window: boolean;
    within_90_day_vdx_window: boolean;
  } | null;
  verdict: { action: string; reason: string };
  claim_form_data: any;
}

interface IVdxClaimSubmitResponse {
  success: boolean;
  status: "submitted" | "rejected" | "error" | "already_tracked";
  message: string | null;
  submission_id: number | null;
  provider_reference: string | null;
  submitted: any;
}

// Backend admin responses can arrive raw or wrapped in { status, message, data, meta }.
function unwrap<T>(d: any): T {
  if (d && typeof d === "object" && "data" in d && d.status !== undefined && d.meta !== undefined) {
    return d.data as T;
  }
  return d as T;
}

// The resolve/check endpoint wants ISO (YYYY-MM-DD); the VDX portal submit keeps DD/MM/YYYY.
// Flexible: accepts 2/7/26, 02/07/2026, etc.
function ddmmyyyyToIso(d: string): string | undefined {
  const m = String(d || "").trim().match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (!m) return undefined;
  const dd = m[1].padStart(2, "0");
  const mm = m[2].padStart(2, "0");
  const yyyy = m[3].length === 2 ? `20${m[3]}` : m[3].padStart(4, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Auto-insert slashes as the agent types digits: 1707 -> 17/07, 17072026 -> 17/07/2026.
function autoSlashDate(v: string): string {
  const digits = v.replace(/\D/g, "").slice(0, 8);
  if (digits.length > 4) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
}

// Treat duplicate-download variants as the same proof: "wow-33845.pdf", "wow-33845 copy.pdf",
// "wow-33845(1).pdf", "wow-33845 (2).pdf" all share the base key "wow-33845".
function proofBaseKey(name: string): string {
  let n = String(name || "").trim().toLowerCase().replace(/\.[a-z0-9]+$/, "");
  n = n.replace(/\s*copy(\s*\d+)?$/, "");
  n = n.replace(/\s*\(\d+\)$/, "");
  return n.trim();
}

function formatWhen(s: any): string {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(d.getTime()) ? String(s) : d.toLocaleString();
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = String(reader.result || "");
      resolve(r.includes(",") ? r.split(",")[1] : r);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const VERDICT_KIND: Record<string, "ok" | "warn" | "bad"> = {
  raise_claim: "ok", wait: "warn", needs_more_info: "warn",
  expired: "bad", already_tracked: "bad", previously_cancelled: "bad",
  not_vdx: "bad", user_not_found: "bad", sprive_plus_inactive: "bad",
};

const VdxClaimForm: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const notify = useNotify();

  const isDark = theme.palette.type === "dark";
  const OK = isDark ? "#4ade80" : "#2e7d32", OK_BG = fade(OK, 0.14), OK_LN = fade(OK, 0.4);
  const WARN = isDark ? "#fbbf24" : "#b26a00", WARN_BG = fade(WARN, 0.14), WARN_LN = fade(WARN, 0.4);
  const BAD = isDark ? "#fb7185" : "#c62828", BAD_BG = fade(BAD, 0.14), BAD_LN = fade(BAD, 0.4);
  const NEUTRAL = theme.palette.text.primary, NEUTRAL_BG = fade(theme.palette.text.primary, 0.08);

  const [email, setEmail] = useState("");
  const [brandName, setBrandName] = useState("");
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState("");
  const [orderId, setOrderId] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const [resolving, setResolving] = useState(false);
  const [check, setCheck] = useState<IVdxCheckResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<IVdxClaimSubmitResponse | null>(null);

  const [merchantId, setMerchantId] = useState<string | null>(null);
  const [merchantOptions, setMerchantOptions] = useState<Array<{ merchant_id: string; merchant_name: string }>>([]);
  const [merchantOpen, setMerchantOpen] = useState(false);
  const [merchantLoading, setMerchantLoading] = useState(false);
  const searchTimer = React.useRef<any>(null);
  const comboRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (comboRef.current && !comboRef.current.contains(e.target as Node)) setMerchantOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const searchBrands = (q: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q || q.trim().length < 2) {
      setMerchantOptions([]);
      setMerchantOpen(false);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setMerchantLoading(true);
      try {
        const config = getConfigGet({
          page: 1, pageSize: 25, sortBy: "merchant_name", sortOrder: "asc", search: q.trim(),
        });
        const res = await axios.get<any>(ENDPOINTS.VDX_BRANDS, config);
        const list = (res.data && (res.data.data || res.data)) || [];
        setMerchantOptions(Array.isArray(list) ? list : []);
        setMerchantOpen(true);
      } catch {
        setMerchantOptions([]);
        setMerchantOpen(true);
      } finally {
        setMerchantLoading(false);
      }
    }, 250);
  };

  const onMerchantInput = (v: string) => {
    setBrandName(v);
    setMerchantId(null);
    searchBrands(v);
  };
  const pickMerchant = (m: { merchant_id: string; merchant_name: string }) => {
    setBrandName(m.merchant_name);
    setMerchantId(m.merchant_id);
    setMerchantOpen(false);
    setMerchantOptions([]);
  };

  const [mode, setMode] = useState<"single" | "bulk" | "history">("single");
  const [historyRows, setHistoryRows] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEmail, setHistoryEmail] = useState("");
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [historyStats, setHistoryStats] = useState<{ total: number; submitted: number; rejected: number; error: number } | null>(null);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [preview, setPreview] = useState<{ items: { url: string; name: string; isPdf: boolean }[]; index: number } | null>(null);
  const [csvRows, setCsvRows] = useState<Array<Record<string, string>>>([]);
  const [csvName, setCsvName] = useState("");
  const [proofFiles, setProofFiles] = useState<File[]>([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState<any>(null);

  const proofNames = new Set(proofFiles.map((f) => f.name));
  const rowFilenames = (r: Record<string, string>) =>
    String(r.attachment || r.attachment_filename || "").split(/[;,]/).map((s) => s.trim()).filter(Boolean);

  // Match by base key so duplicate variants (…copy, (1), (2)) attach to the same row —
  // applies to every source (picker, zip, + add) since it reads proofFiles.
  const rowMatchedFiles = (r: Record<string, string>): File[] => {
    const bases = new Set(rowFilenames(r).map(proofBaseKey));
    return bases.size ? proofFiles.filter((f) => bases.has(proofBaseKey(f.name))) : [];
  };
  const rowOk = (r: Record<string, string>): boolean => {
    const bases = rowFilenames(r).map(proofBaseKey);
    if (!bases.length) return false;
    const have = new Set(rowMatchedFiles(r).map((f) => proofBaseKey(f.name)));
    return bases.every((b) => have.has(b));
  };

  const onCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setCsvName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setCsvRows(parseCsv(String(reader.result || "")));
      setBulkResult(null);
    };
    reader.readAsText(file);
  };

  // One zip holding the CSV + a proof/ folder: unpack both in the browser.
  const onZip = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const entries = await readZip(await file.arrayBuffer());
      let csvText = "";
      const proofs: File[] = [];
      for (const ent of entries) {
        const base = (ent.name.split("/").pop() || ent.name).trim();
        if (!base) continue;
        if (base.toLowerCase().endsWith(".csv")) csvText = new TextDecoder().decode(ent.data);
        else if (/\.(png|jpe?g|pdf|jfif|gif)$/i.test(base)) proofs.push(new File([ent.data], base));
      }
      if (csvText) { setCsvRows(parseCsv(csvText)); setCsvName(file.name); }
      if (proofs.length) setProofFiles(proofs);
      setBulkResult(null);
      if (!csvText && !proofs.length) { notify("No CSV or proof files found in that zip", "warning"); return; }
      notify(`Loaded ${proofs.length} proof file(s)${csvText ? " + CSV" : ""} from the zip`, "info");
    } catch (err: any) {
      notify(err?.message || "Could not read that zip", "error");
    }
  };

  const downloadCsv = () =>
    triggerDownload(new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" }), "vdx-claims-template.csv");

  // The individual proof files named in the sample CSV's `attachment` column.
  const downloadProofFiles = () => {
    triggerDownload(new Blob([b64ToBytes(SAMPLE_PNG_B64)], { type: "image/png" }), "wow-33845.png");
    triggerDownload(new Blob([b64ToBytes(SAMPLE_JPG_B64)], { type: "image/jpeg" }), "boots-99012.jpg");
  };

  // A zip that mirrors the expected upload: the CSV plus a proof/ folder whose
  // filenames match the CSV's `attachment` column.
  const downloadSamples = () => {
    const readme =
      "VDX bulk claim samples\n\n" +
      "1. claims_sample.csv — one row per claim. The `attachment` column names that row's proof file.\n" +
      "2. proof/ — the proof-of-purchase for each row (png/jpg/pdf, <=32MB each). Filenames must match the CSV exactly.\n" +
      "   For several proofs on one row, list the filenames in the attachment column separated by a semicolon: a.png;b.png\n\n" +
      "In the admin: pick the CSV, then select all proof files together. Rows without a matching proof file are skipped.";
    const entries = [
      { name: "claims_sample.csv", data: strToBytes(SAMPLE_CSV) },
      { name: "README.txt", data: strToBytes(readme) },
      { name: "proof/wow-33845.png", data: b64ToBytes(SAMPLE_PNG_B64) },
      { name: "proof/boots-99012.jpg", data: b64ToBytes(SAMPLE_JPG_B64) },
    ];
    triggerDownload(buildZip(entries), "vdx-claim-samples.zip");
  };

  const loadHistory = async (termArg?: string, page = 0) => {
    setHistoryLoading(true);
    try {
      const term = (termArg !== undefined ? termArg : historyEmail).trim();
      const idParams: any = term ? (/^\d+$/.test(term) ? { user_id: term } : { email: term }) : {};
      const config = getConfigGet({ ...idParams, limit: HISTORY_PAGE_SIZE, skip: page * HISTORY_PAGE_SIZE });
      const res = await axios.get<any>(ENDPOINTS.VDX_CLAIM_SUBMIT, config);
      const payload = unwrap<any>(res.data);
      const rows = payload?.data ?? (Array.isArray(payload) ? payload : []);
      setHistoryRows(Array.isArray(rows) ? rows : []);
      setHistoryStats(payload?.stats ?? null);
      setHistoryTotal(Number(payload?.total ?? (Array.isArray(rows) ? rows.length : 0)));
      setHistoryPage(page);
      setHistoryLoaded(true);
    } catch (err: any) {
      notify(err?.response?.data?.message || err?.message || "Failed to load sent claims", "error");
    } finally {
      setHistoryLoading(false);
    }
  };

  React.useEffect(() => {
    if (mode === "history" && !historyLoaded) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Proof picker also accepts a .zip — if one is dropped, pull the proof files out of it.
  // Missing-proof upload: rename the file to the CSV's expected filename so the row matches.
  const addProofForRow = (targetName: string, file: File) => {
    const renamed = new File([file], targetName, { type: file.type });
    setProofFiles((prev) => [...prev.filter((f) => f.name !== targetName), renamed]);
  };
  // Row has no attachment named in the CSV: point that row at the uploaded file.
  const attachProofToRow = (rowIndex: number, file: File) => {
    setCsvRows((rows) => rows.map((r, idx) => (idx === rowIndex ? { ...r, attachment: file.name } : r)));
    setProofFiles((prev) => [...prev.filter((f) => f.name !== file.name), file]);
  };
  // Add an extra proof to a row that already has one (append its name to the attachment column).
  const addExtraProofToRow = (rowIndex: number, file: File) => {
    setProofFiles((prev) => [...prev.filter((f) => f.name !== file.name), file]);
    setCsvRows((rows) => rows.map((r, idx) => {
      if (idx !== rowIndex) return r;
      const names = String(r.attachment || r.attachment_filename || "").split(/[;,]/).map((s) => s.trim()).filter(Boolean);
      if (!names.includes(file.name)) names.push(file.name);
      return { ...r, attachment: names.join(";") };
    }));
  };

  const openPreview = (files: File | File[], startIndex = 0) => {
    const arr = Array.isArray(files) ? files : [files];
    const items = arr.map((f) => ({
      url: URL.createObjectURL(f),
      name: f.name,
      isPdf: /\.pdf$/i.test(f.name) || f.type === "application/pdf"
    }));
    if (!items.length) return;
    setPreview({ items, index: Math.min(Math.max(startIndex, 0), items.length - 1) });
  };
  const closePreview = () => {
    if (preview) preview.items.forEach((it) => URL.revokeObjectURL(it.url));
    setPreview(null);
  };

  const onProofPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fl = e.target.files ? Array.from(e.target.files) : [];
    const zip = fl.find((f) => /\.zip$/i.test(f.name));
    if (!zip) { setProofFiles(fl); return; }
    try {
      const entries = await readZip(await zip.arrayBuffer());
      const proofs = entries
        .filter((ent) => /\.(png|jpe?g|pdf|jfif|gif)$/i.test(ent.name))
        .map((ent) => new File([ent.data], ent.name.split("/").pop() || ent.name));
      setProofFiles(proofs);
      notify(`Loaded ${proofs.length} proof file(s) from the zip`, "info");
    } catch (err: any) {
      notify(err?.message || "Could not read that zip", "error");
    }
  };

  const submitBulk = async () => {
    if (csvRows.length === 0) { notify("Upload a CSV first", "warning"); return; }
    setBulkSubmitting(true);
    setBulkResult(null);
    try {
      // Per row, attach every base-key match (so "…copy"/"(1)" variants go too);
      // send the actual matched filenames, base64 keyed by real name.
      const attachments: Record<string, string> = {};
      const rows = [];
      for (const r of csvRows) {
        const matched = rowMatchedFiles(r);
        for (const f of matched) {
          if (!attachments[f.name]) attachments[f.name] = await fileToBase64(f);
        }
        rows.push({
          email: r.email, merchant: r.merchant, date: r.date, amount: r.amount,
          orderid: r.orderid, description: r.description,
          attachment_filename: matched.map((f) => f.name).join(";"),
        });
      }
      const res = await axios.post(
        ENDPOINTS.VDX_CLAIM_SUBMIT,
        { rows, attachments, source: "bulk" },
        { headers: getConfigPost(), timeout: 5 * 60 * 1000 }
      );
      const data = unwrap<any>(res.data);
      setBulkResult(data);
      notify(`Bulk done — ${data?.summary?.submitted || 0} submitted`, "info");
    } catch (err: any) {
      notify(err?.response?.data?.message || err?.message || "Bulk submit failed", "error");
    } finally {
      setBulkSubmitting(false);
    }
  };

  const memberId = check?.user?.vdx_member_id || null;
  const canSubmit = Boolean(memberId) && check?.verdict?.action === "raise_claim";

  const bulkReady = csvRows.filter(rowOk).length;

  // The email field doubles as a user-id field: a pure number is sent as user_id.
  const idPayload = () => {
    const t = email.trim();
    return /^\d+$/.test(t) ? { user_id: Number(t) } : { email: t };
  };

  const resolve = async () => {
    if (!email.trim() || !brandName.trim()) {
      notify("Enter both customer email/user id and merchant to resolve", "warning");
      return;
    }
    setResolving(true);
    setCheck(null);
    setResult(null);
    try {
      const res = await axios.post(
        ENDPOINTS.VDX_CHECK,
        {
          ...idPayload(),
          brand_name: brandName.trim(),
          purchase_date: ddmmyyyyToIso(date) || undefined,
          amount: amount ? Number(String(amount).replace(/[£$,\s]/g, "")) : undefined,
          order_id: orderId.trim() || undefined,
        },
        { headers: getConfigPost(), timeout: 60000 }
      );
      const data = unwrap<IVdxCheckResponse>(res.data);
      setCheck(data);
      if (data?.brand?.canonical_name) setBrandName(data.brand.canonical_name);
      notify(`Verdict: ${data?.verdict?.action || "unknown"}`, "info");
    } catch (err: any) {
      notify(err?.response?.data?.message || err?.message || "Resolve failed", "error");
    } finally {
      setResolving(false);
    }
  };

  const submit = async () => {
    if (!canSubmit || !memberId) return;
    setSubmitting(true);
    setResult(null);
    try {
      const attachments = await Promise.all(
        files.map(async (f) => ({
          filename: f.name,
          content_type: f.type || undefined,
          data_base64: await fileToBase64(f),
        }))
      );
      const res = await axios.post(
        ENDPOINTS.VDX_CLAIM_SUBMIT,
        {
          ...idPayload(),
          member_id: memberId,
          merchant: brandName.trim(),
          date: date.trim(),
          amount: amount.trim(),
          orderid: orderId.trim(),
          description: description.trim(),
          attachments,
          source: "single",
        },
        { headers: getConfigPost(), timeout: 120000 }
      );
      const data = unwrap<IVdxClaimSubmitResponse>(res.data);
      setResult(data);
      notify(
        data.success ? "Claim submitted" : `Not submitted: ${data.status}`,
        data.success ? "info" : "warning"
      );
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Submit failed";
      const fieldErrors = err?.response?.data?.errors;
      notify(fieldErrors ? `${msg}: ${Object.values(fieldErrors).join(", ")}` : msg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const verdictColors = (action: string) => {
    const k = VERDICT_KIND[action] || "warn";
    if (k === "ok") return { color: OK, background: OK_BG, borderColor: OK_LN };
    if (k === "bad") return { color: BAD, background: BAD_BG, borderColor: BAD_LN };
    return { color: WARN, background: WARN_BG, borderColor: WARN_LN };
  };
  const pillStyle = (good: boolean) => ({
    background: good ? OK_BG : BAD_BG,
    color: good ? OK : BAD,
  });
  const resultColors = () => {
    if (!result) return { c: OK, bg: OK_BG, ln: OK_LN };
    if (result.success) return { c: OK, bg: OK_BG, ln: OK_LN };
    if (result.status === "already_tracked") return { c: WARN, bg: WARN_BG, ln: WARN_LN };
    return { c: BAD, bg: BAD_BG, ln: BAD_LN };
  };

  // Append across multiple picks (dedupe by name) so proof files build up instead of replacing.
  const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files ? Array.from(e.target.files) : [];
    setFiles((prev) => {
      const byName = new Map(prev.map((f) => [f.name, f]));
      picked.forEach((f) => byName.set(f.name, f));
      return Array.from(byName.values());
    });
    e.target.value = "";
  };

  const onEnterResolve = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !merchantOpen) { e.preventDefault(); resolve(); }
  };

  const submitDisabledReason = !check
    ? "Resolve a customer first"
    : !canSubmit
    ? `Verdict is “${check.verdict.action}” — only raise_claim can be submitted`
    : "";

  const step2Active = canSubmit;
  const submitted = !!result?.success;
  const rc = resultColors();

  return (
    <div className={classes.wrap}>
      <div className={classes.band}>
        <div>
          <div className={classes.eyebrow}>VDX · Valuedynamx claims</div>
          <Typography className={classes.bandTitle}>Raise VDX Claim</Typography>
          <p className={classes.bandSub}>
            Submit a missing-transaction claim on the member's behalf. Enter the customer's email — the
            member id and eligibility are resolved for you.
          </p>
        </div>
        <div className={classes.bandBadge}><ReceiptIcon /></div>
      </div>

      <div className={classes.tabs} style={{ marginTop: 18 }}>
        <button className={`${classes.tab} ${mode === "single" ? classes.tabOn : ""}`} onClick={() => setMode("single")}>Single claim</button>
        <button className={`${classes.tab} ${mode === "bulk" ? classes.tabOn : ""}`} onClick={() => setMode("bulk")}>Bulk upload</button>
        <button className={`${classes.tab} ${mode === "history" ? classes.tabOn : ""}`} onClick={() => setMode("history")}>Sent claims</button>
      </div>

      {mode === "single" && (
      <>
      <div className={classes.rail}>
        <div className={classes.step}>
          <span className={`${classes.stepNum} ${check ? classes.stepNumDone : ""}`}>{check ? "✓" : "1"}</span>
          <span className={`${classes.stepLbl} ${check ? classes.stepLblOn : ""}`}>Resolve customer</span>
        </div>
        <span className={classes.railBar} style={check ? { background: submitted ? OK : theme.palette.primary.main } : undefined} />
        <div className={classes.step}>
          <span className={`${classes.stepNum} ${submitted ? classes.stepNumDone : step2Active ? classes.stepNumActive : ""}`}>{submitted ? "✓" : "2"}</span>
          <span className={`${classes.stepLbl} ${step2Active || submitted ? classes.stepLblOn : ""}`}>Submit claim</span>
        </div>
      </div>

      {/* Customer & purchase */}
      <div className={classes.card}>
        <div className={classes.chead}>
          <span className={classes.cheadTitle}>Customer &amp; purchase</span>
          <PersonIcon fontSize="small" htmlColor={theme.palette.text.secondary} />
        </div>
        <div className={classes.cbody}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Customer email or user id" required fullWidth variant="outlined" size="small"
                value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={onEnterResolve} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className={classes.comboWrap} ref={comboRef}>
                <TextField label="Merchant" required fullWidth variant="outlined" size="small"
                  placeholder="Search 2,000+ VDX brands…"
                  helperText={merchantId ? `Selected · #${merchantId}` : "Type to search, then pick the exact VDX brand"}
                  value={brandName}
                  onChange={(e) => onMerchantInput(e.target.value)}
                  onFocus={() => { if (merchantOptions.length) setMerchantOpen(true); }}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" htmlColor={theme.palette.text.secondary} />
                      </InputAdornment>
                    ),
                    endAdornment: merchantLoading ? <CircularProgress size={16} /> : undefined,
                  }} />
                {merchantOpen && (
                  <Paper className={classes.menu} elevation={4}>
                    {merchantOptions.length > 0 ? (
                      merchantOptions.map((m) => (
                        <div key={m.merchant_id} className={classes.option}
                          onMouseDown={(e) => { e.preventDefault(); pickMerchant(m); }}>
                          <span className={classes.optName}>{m.merchant_name}</span>
                          <span className={classes.optId}>#{m.merchant_id}</span>
                        </div>
                      ))
                    ) : (
                      <div className={classes.menuEmpty}>{merchantLoading ? "Searching…" : "No matching brand"}</div>
                    )}
                  </Paper>
                )}
              </div>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Purchase date" placeholder="DD/MM/YYYY" fullWidth variant="outlined" size="small"
                value={date} onChange={(e) => setDate(autoSlashDate(e.target.value))} onKeyDown={onEnterResolve}
                InputProps={{ style: { fontFamily: MONO } }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Amount" fullWidth variant="outlined" size="small"
                value={amount} onChange={(e) => setAmount(e.target.value)} onKeyDown={onEnterResolve}
                InputProps={{ style: { fontFamily: MONO }, startAdornment: <InputAdornment position="start">£</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Order id" fullWidth variant="outlined" size="small"
                value={orderId} onChange={(e) => setOrderId(e.target.value)} onKeyDown={onEnterResolve}
                InputProps={{ style: { fontFamily: MONO } }} />
            </Grid>
          </Grid>

          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button variant="outlined" color="primary" onClick={resolve} disabled={resolving}
              startIcon={resolving ? <CircularProgress size={16} /> : <SearchIcon />}>
              {resolving ? "Resolving…" : check ? "Re-check" : "Resolve & check"}
            </Button>
          </Box>

          {check && (
            <>
              <div className={classes.tiles}>
                <div className={classes.tile}>
                  <div className={classes.tileK}>Member id</div>
                  <div className={classes.tileV}>{memberId || "—"}</div>
                </div>
                <div className={classes.tile}>
                  <div className={classes.tileK}>Sprive Plus</div>
                  <div className={classes.tileV}>
                    <span className={classes.pill} style={pillStyle(!!check.user?.sprive_plus_active)}>
                      <span className={classes.pillDot} />
                      {check.user?.sprive_plus_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
                <div className={classes.tile}>
                  <div className={classes.tileK}>Tracked?</div>
                  <div className={classes.tileV}>
                    <span className={classes.pill}
                      style={check.purchase?.already_tracked ? { background: BAD_BG, color: BAD } : { background: NEUTRAL_BG, color: NEUTRAL }}>
                      {check.purchase?.already_tracked ? "Already tracked" : "Not tracked"}
                    </span>
                  </div>
                </div>
                <div className={classes.tile}>
                  <div className={classes.tileK}>Window</div>
                  <div className={classes.tileV}>
                    {check.purchase?.business_days_since_purchase != null
                      ? `${check.purchase.business_days_since_purchase} bd`
                      : "—"}
                  </div>
                </div>
              </div>

              <div className={classes.verdict} style={verdictColors(check.verdict.action)}>
                {VERDICT_KIND[check.verdict.action] === "ok" ? <CheckCircleIcon />
                  : VERDICT_KIND[check.verdict.action] === "bad" ? <ErrorIcon /> : <InfoIcon />}
                <div>
                  <div className={classes.verdictT}>{check.verdict.action}</div>
                  <div className={classes.verdictS}>{check.verdict.reason}</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Claim details */}
      <div className={`${classes.card} ${!canSubmit ? classes.cardLocked : ""}`}>
        <div className={classes.chead}>
          <span className={classes.cheadTitle}>Claim details</span>
          {check && (
            canSubmit
              ? <span className={classes.pill} style={{ background: OK_BG, color: OK }}><span className={classes.pillDot} />Ready</span>
              : <span className={classes.pill} style={{ background: WARN_BG, color: WARN }}>Locked — verdict must be raise_claim</span>
          )}
        </div>
        <div className={classes.cbody}>
          {!canSubmit && (
            <div className={classes.lockedHint}>
              <LockIcon fontSize="small" />
              <span>{check ? "This customer isn't eligible to submit — the verdict must be raise_claim." : "Resolve a customer above to unlock the claim."}</span>
            </div>
          )}
          <Box mb={2}>
            <TextField label="Description of the issue" fullWidth multiline rows={3} variant="outlined" size="small"
              value={description} onChange={(e) => setDescription(e.target.value)} disabled={!canSubmit} />
          </Box>

          <span className={classes.flabel}>Proof of purchase (optional)</span>
          <label className={classes.drop} style={!canSubmit ? { opacity: 0.5, pointerEvents: "none" } : {}}>
            <CloudUploadIcon className={classes.dropIcon} />
            <div style={{ marginTop: 4, color: theme.palette.text.primary, fontWeight: 600 }}>Drop files or click to upload</div>
            <div className={classes.hint} style={{ marginTop: 3 }}>Select several at once, or click again to add more · png · jpg · pdf · gif — up to 32MB total</div>
            <input hidden type="file" multiple accept=".png,.jpg,.jpeg,.pdf,.jfif,.gif" onChange={onPickFiles} />
          </label>
          {files.length > 0 && (
            <div className={classes.files}>
              {files.map((f) => (
                <span key={f.name} className={classes.fchip}>
                  <span className={classes.fchipName} onClick={() => openPreview(f)} title="Preview">
                    {f.name} · {Math.round(f.size / 1024)} KB
                  </span>
                  <span className={classes.fchipX} onClick={() => setFiles(files.filter((x) => x !== f))}>
                    <CloseIcon style={{ fontSize: 12 }} />
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className={classes.cfoot}>
          <span className={classes.hint}>Submitted server-side — the VDX token never reaches the browser.</span>
          <Tooltip title={submitDisabledReason} disableHoverListener={!submitDisabledReason} arrow>
            <span>
              <Button variant="contained" color="primary" onClick={submit} disabled={!!submitDisabledReason || submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}>
                {submitting ? "Submitting…" : "Submit claim"}
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className={classes.result} style={{ background: rc.bg, border: `1px solid ${rc.ln}` }}>
          {result.success ? <CheckCircleIcon htmlColor={rc.c} />
            : result.status === "already_tracked" ? <InfoIcon htmlColor={rc.c} />
            : <ErrorIcon htmlColor={rc.c} />}
          <div>
            <div className={classes.resultT} style={{ color: rc.c }}>
              {result.success ? "Claim submitted"
                : result.status === "already_tracked" ? "Already tracked — no claim needed"
                : result.status === "rejected" ? "Rejected by VDX"
                : "Submission error"}
            </div>
            <div className={classes.resultS}>
              {result.message}
              {result.submission_id ? ` · logged #${result.submission_id}` : ""}
            </div>
          </div>
        </div>
      )}

      </>
      )}

      {mode === "bulk" && (
        <>
          <div className={classes.card}>
            <div className={classes.chead}>
              <span className={classes.cheadTitle}>Upload claims &amp; proof</span>
            </div>
            <div className={classes.cbody}>
              {/* Option A — one zip, the ops-friendly path */}
              <div className={classes.optionBox}>
                <div className={classes.optionHead}>
                  <span className={classes.optionTag}>Easiest</span>
                  <span className={classes.optionTitle}>Upload one ZIP — claims + proof together</span>
                </div>
                <div className={classes.optionSteps}>
                  1. Download the sample zip.&nbsp;&nbsp; 2. Open it and replace the rows in <b>claims_sample.csv</b> and the files in the <b>proof/</b> folder — keep each proof's filename the same as the CSV's <b>attachment</b> column.&nbsp;&nbsp; 3. Compress the folder back into a .zip and upload it here.
                </div>
                <Box display="flex" gridGap={10} flexWrap="wrap" mt={1.5}>
                  <Button variant="outlined" startIcon={<CloudDownloadIcon />} onClick={downloadSamples}>Download sample .zip</Button>
                  <Button variant="contained" color="primary" component="label" startIcon={<AttachFileIcon />}>
                    Upload ZIP
                    <input hidden type="file" accept=".zip,application/zip" onChange={onZip} />
                  </Button>
                </Box>
                {(csvRows.length > 0 || proofFiles.length > 0) && (
                  <div className={classes.hint} style={{ marginTop: 10 }}>
                    Loaded <b>{csvName || "—"}</b> · {csvRows.length} rows · {proofFiles.length} proof file(s)
                  </div>
                )}
              </div>

              {/* Option B — separate files, collapsed by default */}
              <details className={classes.optionBox}>
                <summary className={classes.optionSummary}>Or upload the CSV and proof files separately</summary>
                <div style={{ marginTop: 12 }}>
                  <Box display="flex" gridGap={8} flexWrap="wrap">
                    <Button variant="outlined" size="small" startIcon={<CloudDownloadIcon />} onClick={downloadCsv}>Sample CSV</Button>
                    <Button variant="outlined" size="small" startIcon={<CloudDownloadIcon />} onClick={downloadProofFiles}>Sample proof files</Button>
                  </Box>
                  <Grid container spacing={2} style={{ marginTop: 4 }}>
                    <Grid item xs={12} sm={6}>
                      <span className={classes.flabel}>Claims CSV</span>
                      <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>
                        {csvRows.length ? `${csvName || "CSV"} · ${csvRows.length} rows` : "Choose CSV"}
                        <input hidden type="file" accept=".csv" onChange={onCsv} />
                      </Button>
                      <div className={classes.hint} style={{ marginTop: 6 }}>
                        Columns: email · merchant · date · amount · orderid · description · attachment.
                        For several proofs on one row, separate filenames with a semicolon (a.png;b.png).
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <span className={classes.flabel}>Proof files</span>
                      <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                        {proofFiles.length ? `${proofFiles.length} files selected` : "Choose proof files (or a .zip)"}
                        <input hidden type="file" multiple accept=".png,.jpg,.jpeg,.pdf,.jfif,.gif,.zip"
                          onChange={onProofPick} />
                      </Button>
                      <div className={classes.hint} style={{ marginTop: 6 }}>Filenames match the CSV's attachment column — duplicate variants (…copy, (1), (2)) attach to the same row automatically · png/jpg/pdf, ≤32MB</div>
                    </Grid>
                  </Grid>
                </div>
              </details>
            </div>
          </div>

          {csvRows.length > 0 && (
            <div className={classes.card}>
              <div className={classes.chead}>
                <span className={classes.cheadTitle}>Preview &amp; validation</span>
                <span className={classes.summaryRow}>
                  <span><b style={{ color: OK }}>{bulkReady}</b> ready</span>
                  <span><b style={{ color: WARN }}>{csvRows.length - bulkReady}</b> no proof</span>
                </span>
              </div>
              <div className={classes.cbody}>
                <div className={classes.tblWrap}>
                  <table className={classes.tbl}>
                    <thead><tr>
                      <th className={classes.th}>Email</th><th className={classes.th}>Merchant</th>
                      <th className={classes.th}>Date</th><th className={classes.th}>Amount</th>
                      <th className={classes.th}>Order id</th><th className={classes.th}>Proof</th>
                    </tr></thead>
                    <tbody>
                      {csvRows.map((r, i) => {
                        const fns = rowFilenames(r);
                        const matched = rowMatchedFiles(r);
                        const ok = rowOk(r);
                        return (
                          <tr key={i}>
                            <td className={classes.td}>{r.email}</td>
                            <td className={classes.td}>{r.merchant}</td>
                            <td className={classes.td}>{r.date}</td>
                            <td className={classes.td}>£{String(r.amount || "").replace(/[£$]/g, "")}</td>
                            <td className={classes.td}>{r.orderid}</td>
                            <td className={classes.td} style={{ color: fns.length === 0 || !ok ? WARN : OK }}>
                              {fns.length === 0 ? "— none" : ok ? `✓ matched${matched.length > 1 ? ` (${matched.length} files)` : ""}` : "✕ missing file"}
                              {ok && (
                                <>
                                  <span className={classes.fchipName} style={{ marginLeft: 8, color: theme.palette.primary.main }}
                                    onClick={() => { if (matched.length) openPreview(matched); }}>
                                    view{matched.length > 1 ? ` (${matched.length})` : ""}
                                  </span>
                                  <label className={classes.fchipName} style={{ marginLeft: 8, color: theme.palette.primary.main }}>
                                    + add
                                    <input hidden type="file" accept=".png,.jpg,.jpeg,.pdf,.jfif,.gif"
                                      onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) addExtraProofToRow(i, f); }} />
                                  </label>
                                </>
                              )}
                              {!ok && (
                                <label className={classes.fchipName} style={{ marginLeft: 8, color: theme.palette.primary.main }}>
                                  {fns.length === 0 ? "attach" : "upload"}
                                  <input hidden type="file" accept=".png,.jpg,.jpeg,.pdf,.jfif,.gif"
                                    onChange={(e) => {
                                      const f = e.target.files && e.target.files[0];
                                      if (!f) return;
                                      if (fns.length > 0) addProofForRow(fns.find((n) => !proofNames.has(n)) || fns[0], f);
                                      else attachProofToRow(i, f);
                                    }} />
                                </label>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className={classes.cfoot}>
                <span className={classes.hint}>Per row: member id resolved from email · already-tracked checked · logged.</span>
                <Button variant="contained" color="primary" onClick={submitBulk} disabled={bulkSubmitting}
                  startIcon={bulkSubmitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}>
                  {bulkSubmitting ? "Submitting…" : `Submit ${csvRows.length} claims`}
                </Button>
              </div>
            </div>
          )}

          {bulkResult && (
            <div className={classes.card}>
              <div className={classes.chead}>
                <span className={classes.cheadTitle}>Results</span>
                <span className={classes.summaryRow}>
                  <span><b style={{ color: OK }}>{bulkResult.summary?.submitted || 0}</b> submitted</span>
                  <span><b style={{ color: WARN }}>{(bulkResult.summary?.skipped || 0) + (bulkResult.summary?.already_tracked || 0)}</b> skipped</span>
                  <span><b style={{ color: BAD }}>{(bulkResult.summary?.rejected || 0) + (bulkResult.summary?.error || 0)}</b> failed</span>
                </span>
              </div>
              <div className={classes.cbody}>
                <div className={classes.tblWrap}>
                  <table className={classes.tbl}>
                    <thead><tr>
                      <th className={classes.th}>Email</th><th className={classes.th}>Merchant</th>
                      <th className={classes.th}>Status</th><th className={classes.th}>Detail</th>
                    </tr></thead>
                    <tbody>
                      {(bulkResult.results || []).map((r: any, i: number) => (
                        <tr key={i}>
                          <td className={classes.td}>{r.email}</td>
                          <td className={classes.td}>{r.merchant}</td>
                          <td className={classes.td} style={{ color: r.status === "submitted" ? OK : (r.status === "already_tracked" || r.status === "skipped") ? WARN : BAD }}>{r.status}</td>
                          <td className={classes.td} style={{ whiteSpace: "normal", fontFamily: "inherit" }}>{r.message}{r.submission_id ? ` · #${r.submission_id}` : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {mode === "history" && (
        <div className={classes.card}>
          <div className={classes.chead}>
            <span className={classes.cheadTitle}>Sent claims{historyEmail.trim() ? " · filtered" : ""}</span>
          </div>
          <div className={classes.cbody}>
            <div className={classes.tiles} style={{ gridTemplateColumns: "repeat(4, 1fr)", marginTop: 0 }}>
              <div className={classes.tile}>
                <div className={classes.tileK}>Total sent</div>
                <div className={classes.tileV}>{historyStats?.total ?? 0}</div>
              </div>
              <div className={classes.tile}>
                <div className={classes.tileK}>Submitted</div>
                <div className={classes.tileV} style={{ color: OK }}>{historyStats?.submitted ?? 0}</div>
              </div>
              <div className={classes.tile}>
                <div className={classes.tileK}>Rejected</div>
                <div className={classes.tileV} style={{ color: BAD }}>{historyStats?.rejected ?? 0}</div>
              </div>
              <div className={classes.tile}>
                <div className={classes.tileK}>Errors</div>
                <div className={classes.tileV} style={{ color: WARN }}>{historyStats?.error ?? 0}</div>
              </div>
            </div>
            <Box display="flex" gridGap={10} mt={2} mb={2} alignItems="center" flexWrap="wrap">
              <TextField label="Filter by customer email or user id" variant="outlined" size="small"
                value={historyEmail} onChange={(e) => setHistoryEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); loadHistory(undefined, 0); } }}
                style={{ minWidth: 300 }} />
              <Button variant="outlined" color="primary" onClick={() => loadHistory(undefined, 0)} disabled={historyLoading}
                startIcon={historyLoading ? <CircularProgress size={16} /> : <SearchIcon />}>
                {historyLoading ? "Loading…" : "Search"}
              </Button>
              {historyEmail.trim() && (
                <Button variant="text" size="small" onClick={() => { setHistoryEmail(""); loadHistory("", 0); }}>
                  Clear
                </Button>
              )}
            </Box>
            <div className={classes.hint} style={{ marginBottom: 12 }}>
              {historyEmail.trim()
                ? "Showing this customer's claims."
                : "Showing all VDX claims, newest first."} 10 per page — use the pager below.
            </div>
            {historyRows.length === 0 ? (
              <div style={{ minHeight: 260, display: "flex", alignItems: "center", justifyContent: "center", color: theme.palette.text.secondary, fontSize: 14, border: `1px solid ${theme.palette.divider}`, borderRadius: 10 }}>
                {historyLoading ? "Loading…" : historyEmail.trim() ? "No claims for this customer." : "No claims logged yet."}
              </div>
            ) : (
            <div className={classes.tblWrap}>
              <table className={classes.tbl}>
                <thead><tr>
                  <th className={classes.th}>When</th><th className={classes.th}>Email</th>
                  <th className={classes.th}>Member id</th><th className={classes.th}>Merchant</th>
                  <th className={classes.th}>Amount</th><th className={classes.th}>Status</th>
                  <th className={classes.th}>Source</th><th className={classes.th}>By</th><th className={classes.th}>#</th>
                  <th className={classes.th} />
                </tr></thead>
                <tbody>
                  {historyRows.map((r: any) => (
                    <React.Fragment key={r.id}>
                      <tr>
                        <td className={classes.td}>{formatWhen(r.created_at)}</td>
                        <td className={classes.td} style={{ whiteSpace: "normal", fontFamily: "inherit" }}>{r.email || "—"}</td>
                        <td className={classes.td}>{r.member_id || "—"}</td>
                        <td className={classes.td}>{r.merchant || "—"}</td>
                        <td className={classes.td}>{r.amount != null ? `£${String(r.amount).replace(/[£$]/g, "")}` : "—"}</td>
                        <td className={classes.td} style={{ color: r.status === "submitted" ? OK : r.status === "rejected" ? BAD : WARN }}>{r.status}</td>
                        <td className={classes.td}>{r.source || "—"}</td>
                        <td className={classes.td} style={{ whiteSpace: "normal", fontFamily: "inherit" }}>{r.submitted_by || "—"}</td>
                        <td className={classes.td}>{r.id}</td>
                        <td className={classes.td}>
                          <Button variant="text" size="small" color="primary"
                            onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                            {expandedId === r.id ? "Less" : "More"}
                          </Button>
                        </td>
                      </tr>
                      {expandedId === r.id && (
                        <tr>
                          <td className={classes.detailCell} colSpan={10}>
                            <div className={classes.detailGrid}>
                              {[
                                ["User id", r.user_id],
                                ["Order id", r.order_id],
                                ["Purchase date", r.date],
                                ["Attachments", r.attachment_count],
                                ["Provider", r.provider],
                                ["Provider ref", r.provider_reference],
                                ["Freshdesk ticket", r.freshdesk_ticket_id],
                                ["Description", r.description],
                                ["Provider response", r.response_excerpt],
                              ].map(([k, v]) => (
                                <div key={String(k)} className={classes.detailItem}>
                                  <span className={classes.detailK}>{k}</span>
                                  <span className={classes.detailV}>{v === null || v === undefined || v === "" ? "—" : String(v)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            {historyTotal > HISTORY_PAGE_SIZE && (
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gridGap={10}>
                <span className={classes.hint}>
                  {historyPage * HISTORY_PAGE_SIZE + 1}–{Math.min((historyPage + 1) * HISTORY_PAGE_SIZE, historyTotal)} of {historyTotal}
                </span>
                <Box display="flex" gridGap={8} alignItems="center">
                  <Button variant="outlined" size="small" disabled={historyLoading || historyPage === 0}
                    onClick={() => loadHistory(undefined, historyPage - 1)}>Prev</Button>
                  <span className={classes.hint}>
                    Page {historyPage + 1} of {Math.max(1, Math.ceil(historyTotal / HISTORY_PAGE_SIZE))}
                  </span>
                  <Button variant="outlined" size="small"
                    disabled={historyLoading || (historyPage + 1) * HISTORY_PAGE_SIZE >= historyTotal}
                    onClick={() => loadHistory(undefined, historyPage + 1)}>Next</Button>
                </Box>
              </Box>
            )}
          </div>
        </div>
      )}

      {preview && (
        <Dialog open onClose={closePreview} fullScreen>
          <DialogTitle>
            {preview.items[preview.index].name}
            {preview.items.length > 1 ? `  ·  ${preview.index + 1} of ${preview.items.length}` : ""}
          </DialogTitle>
          <DialogContent dividers style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "#0b1020", padding: 0 }}>
            {preview.items[preview.index].isPdf
              ? <iframe title={preview.items[preview.index].name} src={preview.items[preview.index].url} style={{ width: "100%", height: "calc(100vh - 150px)", border: 0 }} />
              : <img src={preview.items[preview.index].url} alt={preview.items[preview.index].name} style={{ maxWidth: "100%", maxHeight: "calc(100vh - 150px)", objectFit: "contain" }} />}
          </DialogContent>
          <DialogActions>
            {preview.items.length > 1 && (
              <>
                <Button disabled={preview.index === 0} onClick={() => setPreview({ ...preview, index: preview.index - 1 })}>Prev</Button>
                <Button disabled={preview.index === preview.items.length - 1} onClick={() => setPreview({ ...preview, index: preview.index + 1 })}>Next</Button>
              </>
            )}
            <Button onClick={() => window.open(preview.items[preview.index].url, "_blank")}>Open in new tab</Button>
            <Button variant="contained" color="primary" onClick={closePreview}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      <Notification />
    </div>
  );
};

export default VdxClaimForm;
